import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AuthContext } from "./auth";
import { useActiveAccount } from "./active-account";

/**
 * The hub's page data -- games, roster, members, hours -- held for the
 * active account, so moving between Home, Roster, Billing and Settings shows
 * what was already loaded instead of fetching it all again.
 *
 * Each piece loads the first time a page asks for it and then stays put. It
 * loads again only when the account changes (switching accounts, signing
 * out) or the page is refreshed; there is deliberately no background
 * refresh. The viewer's own edits -- adding a player, removing a member,
 * buying hours -- update the stored copy (or reload that one piece), so every
 * page agrees without a refetch. An edit whose effect the viewer can't patch
 * in by hand -- uploading a game, which adds a row to Home and uses hours --
 * invalidates the pieces it changed (`useHubDataInvalidate`), so the next
 * page that shows them loads them again.
 */

export type HubDataStatus = "loading" | "loaded" | "error";

type Entry = {
  status: HubDataStatus;
  data?: unknown;
  /** The request whose answer this entry is waiting for; a newer one wins. */
  promise?: Promise<unknown>;
};

type Store = {
  /** Which account the entries belong to; a change empties the store. */
  accountKey: string;
  get: (key: string) => Entry | undefined;
  load: <T>(key: string, loader: () => Promise<T>, force?: boolean) => Promise<T | undefined>;
  set: <T>(key: string, update: (previous: T | undefined) => T, accountKey: string) => void;
  /** Forgets every entry whose key starts with `prefix`; the next reader loads it afresh. */
  invalidate: (prefix: string) => void;
};

const HubDataContext = createContext<Store | undefined>(undefined);

/**
 * The entries, plus everything that reads and writes them. Shared by the
 * provider (one store for the whole hub) and the hook's fallback (one per
 * component, so a page rendered on its own behaves as it did before).
 */
const useStore = (accountKey: string): Store => {
  const entries = useRef(new Map<string, Entry>());
  const keyRef = useRef(accountKey);
  // Bumped on every change: the store's identity has to change with its
  // contents, or pages reading it through context would never re-render.
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // A different account starts from nothing: its data was never loaded here.
  if (keyRef.current !== accountKey) {
    keyRef.current = accountKey;
    entries.current = new Map();
  }

  const get = useCallback((key: string) => entries.current.get(key), []);

  const load = useCallback(
    <T,>(key: string, loader: () => Promise<T>, force = false): Promise<T | undefined> => {
      const existing = entries.current.get(key);
      if (!force && existing) {
        return (existing.promise as Promise<T> | undefined) ?? Promise.resolve(existing.data as T);
      }
      const owner = keyRef.current;
      const map = entries.current;
      const promise = loader();
      // A reload keeps what's on screen while it runs; a first load has nothing to keep.
      map.set(key, {
        status: existing?.data !== undefined ? existing.status : "loading",
        data: existing?.data,
        promise,
      });
      bump();
      const current = () => keyRef.current === owner && map.get(key)?.promise === promise;
      return promise.then(
        (data) => {
          if (current()) {
            map.set(key, { status: "loaded", data });
            bump();
          }
          return data;
        },
        (error) => {
          if (current()) {
            const previous = map.get(key);
            // A failed reload leaves the last good copy up; the caller reports it.
            map.set(
              key,
              previous?.data !== undefined ? { status: "loaded", data: previous.data } : { status: "error" },
            );
            bump();
          }
          if (force) throw error;
          console.error(`Failed to load ${key}`, error);
          return undefined;
        },
      );
    },
    [bump],
  );

  const set = useCallback(
    <T,>(key: string, update: (previous: T | undefined) => T, forAccount: string) => {
      // An edit that finishes after an account switch belongs to the old account.
      if (forAccount !== keyRef.current) return;
      const previous = entries.current.get(key);
      entries.current.set(key, { status: "loaded", data: update(previous?.data as T | undefined) });
      bump();
    },
    [bump],
  );

  const invalidate = useCallback(
    (prefix: string) => {
      let dropped = false;
      Array.from(entries.current.keys()).forEach((key) => {
        if (key.startsWith(prefix)) {
          entries.current.delete(key);
          dropped = true;
        }
      });
      if (dropped) bump();
    },
    [bump],
  );

  return useMemo(
    () => ({ accountKey, get, load, set, invalidate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountKey, get, load, set, invalidate, version],
  );
};

const accountKeyFor = (userId: string | undefined, account: ReturnType<typeof useActiveAccount>["activeAccount"]) =>
  `${userId ?? ""}|${account?.kind === "team" ? `team:${account.teamId}` : account ? "me" : "none"}`;

export const HubDataProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useContext(AuthContext);
  const { activeAccount } = useActiveAccount();
  const store = useStore(accountKeyFor(user?.userId, activeAccount));
  return <HubDataContext.Provider value={store}>{children}</HubDataContext.Provider>;
};

export type HubData<T> = {
  data: T | undefined;
  status: HubDataStatus;
  /** Fetches again (after the viewer changed it, or to retry); rejects if that fails. */
  reload: () => Promise<T | undefined>;
  /** Replaces the stored copy, e.g. with a row the viewer just added. */
  update: (fn: (previous: T | undefined) => T) => void;
};

/**
 * One piece of hub data, loaded on first use and kept for the account.
 *
 * `key` names the piece within the account ("roster", "sessions:month");
 * pass `null` while there's nothing to load yet. `loader` must fetch exactly
 * what the key names -- it's only called when the store doesn't have it.
 */
export function useHubData<T>(key: string | null, loader: () => Promise<T>): HubData<T> {
  const shared = useContext(HubDataContext);
  // Always called, so the hook order never changes; only used without a provider.
  const local = useStore("local");
  const store = shared ?? local;
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const entry = key ? store.get(key) : undefined;
  const missing = key !== null && entry === undefined;

  useEffect(() => {
    if (key !== null && !store.get(key)) void store.load(key, () => loaderRef.current());
  }, [key, store, missing]);

  const accountKey = store.accountKey;
  const reload = useCallback(
    () => (key === null ? Promise.resolve(undefined) : store.load(key, () => loaderRef.current(), true)),
    [key, store],
  );
  const update = useCallback(
    (fn: (previous: T | undefined) => T) => {
      if (key !== null) store.set(key, fn, accountKey);
    },
    [key, store, accountKey],
  );

  return {
    data: entry?.data as T | undefined,
    status: entry?.status ?? "loading",
    reload,
    update,
  };
}

/**
 * Drops the stored pieces whose keys start with a prefix ("sessions:"), for
 * after a change the viewer made that the store can't patch in itself. A page
 * showing one of them loads it again straight away; any other page does when
 * it next asks. Without a provider there is nothing shared to drop.
 */
export function useHubDataInvalidate(): (prefix: string) => void {
  const shared = useContext(HubDataContext);
  return useCallback((prefix: string) => shared?.invalidate(prefix), [shared]);
}
